
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const DEFAULT_SUBJECTS = [
  '組織論', 'マーケティング', '生産管理', '財務会計',
  '会計学', '租税法', '簿記論', '民法', 'その他',
];

/**
 * 全ページ共通で使える科目一覧フック。
 * user_categories (subject) から全ユーザーの科目を重複なく取得する。
 * 自動追加は行わない。
 */
export const useSubjectList = () => {
  const [subjects, setSubjects] = useState<string[]>(DEFAULT_SUBJECTS);
  const [loading, setLoading] = useState(true);

  const fetchSubjects = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setSubjects(DEFAULT_SUBJECTS);
        setLoading(false);
        return;
      }

      // 全ユーザーの科目を取得（重複なし）
      const { data, error } = await supabase
        .from('user_categories')
        .select('category_name, is_favorite, user_id')
        .eq('category_type', 'subject')
        .order('category_name', { ascending: true });

      if (error) throw error;

      // 重複を除去（自分のレコードを優先してお気に入り状態を反映）
      const nameMap = new Map<string, { name: string; isFav: boolean }>();
      for (const cat of data || []) {
        const existing = nameMap.get(cat.category_name);
        if (!existing) {
          nameMap.set(cat.category_name, { name: cat.category_name, isFav: cat.is_favorite && cat.user_id === user.id });
        } else if (cat.user_id === user.id && cat.is_favorite) {
          nameMap.set(cat.category_name, { name: cat.category_name, isFav: true });
        }
      }

      // お気に入りを先頭に、残りはアルファベット順
      const sorted = Array.from(nameMap.values()).sort((a, b) => {
        if (a.isFav && !b.isFav) return -1;
        if (!a.isFav && b.isFav) return 1;
        return a.name.localeCompare(b.name, 'ja');
      });

      const names = sorted.map((s) => s.name);
      setSubjects(names.length > 0 ? names : DEFAULT_SUBJECTS);
    } catch (err) {
      console.error('科目一覧取得エラー:', err);
      setSubjects(DEFAULT_SUBJECTS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubjects();
  }, [fetchSubjects]);

  return { subjects, loading, refreshSubjects: fetchSubjects };
};

export { DEFAULT_SUBJECTS };
